import { previaDaMensagem } from '../../utils/previaDaMensagem';
import React, { forwardRef, useImperativeHandle, useEffect, useState, useContext } from 'react';
import { PermIdentity, InsertDriveFileOutlined, Audiotrack } from '@material-ui/icons';
import { ehAudio, rotuloDoAnexo } from '../../utils/anexoDaMensagem';

import { useSelector, useDispatch } from 'react-redux'

import useChat from '../../services/socket';

import { setMessage } from '../../redux/notification/notification.actions';
import { Badge, Box, Chip, Grid } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { marcaDaSala } from '../../utils/marcaDaSala';
import { ehSalaDeWhatsapp } from '../../utils/janelaDoWhatsapp';

const Room = forwardRef((props, ref) => {
  const { theme } = useContext(ThemeContext);
  const { room } = props;
  const {
    messages,
    readMessage,
    sendFile,
    totalDeMensagens,
    carregarMaisAntigas
  } = useChat(room.room_id, room);

  /*
   * ESTA LINHA ABRE UM SOCKET SÓ: o da própria conversa.
   *
   * Havia aqui um segundo `useChat(props.selectedRoom)`, guardado em
   * `mensagem`. Como este componente é UMA LINHA da lista, esse segundo
   * socket era aberto por CADA linha -- e todos para a mesma sala, a
   * selecionada. Com 10 conversas na lista, selecionar uma abria 10 conexões
   * para ela e disparava 10 `GET /messages/{id}` idênticos; trocar de sala
   * derrubava as 10 e repetia tudo, porque o `roomId` é dependência do efeito
   * do `useChat`. Era a lentidão relatada ao abrir e trocar de sala.
   *
   * Quem fala pela sala selecionada agora é o `RoomList`, com UMA instância
   * para a lista toda. Ela chega aqui por `enviarNaSalaSelecionada` e
   * `avisarDigitacaoNaSalaSelecionada`, e o `useImperativeHandle` abaixo
   * continua expondo as duas exatamente como antes.
   *
   * O que NÃO podia mudar, e não mudou: a instância da sala selecionada é
   * criada SEM o objeto `room`. É isso que faz `sendMessage` enviar
   * `operatorId: user.id` (o operador logado) em vez de `room.operator_id`
   * (o dono da sala) -- e o ws grava esse campo como `user_id` da mensagem.
   * Numa sala com convidados os dois são pessoas diferentes, então reaproveitar
   * a instância primária aqui atribuiria a fala ao operador errado.
   */

  const [messagesLength, setMessagesLength] = useState(0);

  const dispatch = useDispatch()

  const clientMessage = useSelector(state => state.rooms.clientMessage);
  const searchRoom = props.inProgressRooms.find(room => room.room_id === props.selectedRoom);
  const [unreadCount, setUnreadCount] = useState(0);
  const marca = marcaDaSala(room);
  const linhaSelecionada = props.selectedRoom === room.room_id;

  const messageReadFocus = () => {
    let foundReadMessage = false;

    messages.slice().reverse().forEach(message => {
      if (foundReadMessage) return;

      if (!message.read_at && message.type === 0) {
        readMessage(message.id);
      } else if (message.read_at) {
        foundReadMessage = true;
      }
    });
  }

  useEffect(() => {
    if (searchRoom)
      props.setMessages(searchRoom?.messages);
    // Direciona o usuário para o chat ao pegar um atendimento da fila (Painel de Atendimentos).
    if (props.currentRoomId !== null) {
      if (room.room_id === props.currentRoomId) {
        props.handleSelectRoom(room.client, { ...room, room_id: props.currentRoomId, whatsapp: false })
      }
    }
    // eslint-disable-next-line
  }, []);


  // O aviso "digitando" saiu daqui para o `RoomList`. Ele é GLOBAL (vai para o
  // redux), e rodava em cada linha da lista: N dispatches e N `setTimeout` para
  // um único indicador na tela.
  useEffect(() => {
    if (props.selectedRoom === room.room_id) {
      props.setMessages(messages);
    }
  })

  // Considera a janela "ativa" quando está visível e em foco (igual ao WhatsApp).
  const isWindowActive = () =>
    (typeof document === 'undefined' || !document.hidden) &&
    (typeof document === 'undefined' || typeof document.hasFocus !== 'function' || document.hasFocus());

  useEffect(() => {
    const isSelected = props.selectedRoom === room.room_id;

    // Se o chat está aberto E a janela está em foco/visível, marca as novas
    // mensagens como lidas automaticamente e zera o badge — sem precisar clicar.
    if (isSelected && isWindowActive()) {
      messageReadFocus();
      setUnreadCount(0);
      return;
    }

    // Caso contrário (chat fechado ou janela sem foco), mantém o badge com a
    // quantidade de mensagens não lidas.
    let count = 0;
    messages?.forEach(message => {
      if (!message.read_at && message.type === 0) {
        count++;
      }
    });
    setUnreadCount(count);
    // eslint-disable-next-line
  }, [messages.length, props.selectedRoom]);

  // Enquanto o chat está aberto, ao voltar o foco/visibilidade da janela também
  // marca as mensagens como lidas (WhatsApp: recebeu com a janela em background e
  // depois voltou para ela).
  useEffect(() => {
    if (props.selectedRoom !== room.room_id) return;

    const handleActive = () => {
      if (isWindowActive()) {
        messageReadFocus();
        setUnreadCount(0);
      }
    };

    window.addEventListener('focus', handleActive);
    document.addEventListener('visibilitychange', handleActive);

    return () => {
      window.removeEventListener('focus', handleActive);
      document.removeEventListener('visibilitychange', handleActive);
    };
    // eslint-disable-next-line
  }, [props.selectedRoom, room.room_id, messages.length]);

  useEffect(() => {
    if (messagesLength !== messages.length) {
      setMessagesLength(messages.length)
      /*
       * O `dispatch(setLastMessages(...))` que ficava aqui saiu.
       *
       * Seu unico leitor era o `checkTime` do `Chat`, o relogio de inatividade
       * que mudou para o servidor (`chat:encerrar-inativos`). Sem leitor, ele
       * era estado morto -- mas CARO: `RoomList` conecta a fatia `rooms`
       * inteira, entao cada escrita em `lastMessages` criava um objeto novo e
       * renderizava a tela do chat de ponta a ponta. Uma vez por mensagem, por
       * conversa da lista.
       */
    }

    if (props.selectedRoom === room.room_id) {
      props.setMessages(messages);
      dispatch(setMessage(messages))
    }

    if (messagesLength !== messages.length && messagesLength !== 0 && messages[messages.length - 1] && messages[messages.length - 1].type === 0) {
      setMessagesLength(messages.length);
    }
    // eslint-disable-next-line
  }, [messages.length, props.selectedRoom]);

  /**
   * Quantas mensagens a sala TEM -- o que diz ao `Chat` se ainda ha historico
   * mais antigo para buscar quando o operador rola ate o topo.
   *
   * Efeito PROPRIO, e nao uma linha a mais no efeito das mensagens, por causa
   * da ORDEM: a primeira resposta grava a lista e o total em dois `setState`
   * seguidos dentro de um `.then()`, e no React 17 isso nao e agrupado -- sao
   * dois renders. O efeito das mensagens so dispara no primeiro, quando o
   * total ainda e zero, e nao voltaria a disparar. O botao nunca apareceria.
   */
  useEffect(() => {
    if (props.selectedRoom !== room.room_id) return;
    if (props.setTotalDeMensagens) props.setTotalDeMensagens(totalDeMensagens);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDeMensagens, props.selectedRoom, room.room_id]);

  useImperativeHandle(ref, () => ({
    sendMessage: (message, user, id, type, whatsapp) => {
      props.enviarNaSalaSelecionada?.(message, user, id, type, whatsapp);
    },
    getMessages: () => {
      return messages;
    },
    readMessage: id => {
      readMessage(id);
    },
    sendingMessageEvent: (event) => {
      props.avisarDigitacaoNaSalaSelecionada?.(event);
    },
    sendFile: file => {
      sendFile(file);
    },
    setUnreadCount: count => {
      setUnreadCount(count);
    },
    getUnreadCount: () => {
      return unreadCount;
    },
    messageReadFocus: () => {
      messageReadFocus();
    },
    // O historico mais antigo, sob demanda. Vai para o `Chat` pelo
    // `selectedRoomRef`, como `sendMessage` e `readMessage` ja vao.
    carregarMaisAntigas: () => {
      carregarMaisAntigas();
    }
  }));

  const date = createdAt => {
    if (createdAt) {
      var time = createdAt.split("T");
      time = time[1].split(":");
      return `${time[0]}:${time[1]}`
    }

    return `${new Date().getHours()}:${new Date().getMinutes()}`;
  };

  /*
   * A prévia é TEXTO, não HTML.
   *
   * Até 27/10/2026 esta linha desenhava a mensagem com
   * `dangerouslySetInnerHTML`. A mensagem de boas-vindas traz a marca da
   * SysFar num `<img>`, e a imagem entrava com o tamanho dela: o texto ficava
   * por baixo, ilegível (Slack da Gisele, 26/10). Ela descreveu como problema
   * de cor; é a imagem ocupando a linha.
   *
   * `noWrap` + `textOverflow` porque a lista é de uma linha só: sem isso, uma
   * mensagem longa empurra a largura da linha e desalinha a lista inteira.
   */
  const PreviaDeTexto = ({ conteudo }) => (
    <Grid
      sx={{
        margin: '0 8px',
        bgcolor: 'transparent',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        /*
         * `color: 'inherit'` não é enfeite: sem ele a prévia some.
         *
         * A linha de fora escolhe a cor conforme a sala esteja selecionada
         * (branco sobre o navy) e escreve isso num `style` inline. Mas o tema
         * registra `color` em TODO `MuiGrid` (`MuiGrid.styleOverrides.root`),
         * então este Grid se repinta com `text.primary` e a cor de fora nunca
         * chega.
         *
         * No tema escuro ninguém notava, porque `text.primary` já é claro. No
         * CLARO ele é cinza-escuro, e sobre o navy da linha selecionada dava
         * 1,17:1 -- a prévia da conversa aberta ficava invisível (relatado em
         * 29/10/2026). Herdando, volta a 8,5:1.
         */
        color: 'inherit',
      }}
    >
      {previaDaMensagem(conteudo)}
    </Grid>
  );

  const lastMessage = () => {
    let ret = <Grid item ></Grid>;

    if (messages.length) {
      let message = messages[messages.length - 1];
      if (!message.archive) {
        // `clientMessage` guarda a SALA em que o cliente está digitando, e a
        // comparação é o conserto: enquanto era um `true` global, o cliente de
        // uma conversa digitava e TODAS as linhas da lista trocavam a prévia da
        // última mensagem por este aviso.
        if (clientMessage && clientMessage === room.room_id) {
          ret = 'Cliente está digitando ....'
        } else {
          ret = <PreviaDeTexto conteudo={message.message} />;
        }
      } else {
        /*
         * Áudio aparece como "Áudio", não como `683f1a2c9b.ogg` (ponto 9 da
         * Gisele, 20/10). O nome do arquivo não diz nada a quem está
         * decidindo qual conversa atender primeiro -- e o nome é gerado, não
         * escolhido por ninguém.
         *
         * A regra de "isto é áudio" mora em `utils/anexoDaMensagem`, e é mais
         * larga que a do `Chat` de propósito: ver o comentário de lá.
         */
        const audio = ehAudio(message.archive);

        ret = (
          /* Mesmo motivo do `PreviaDeTexto`: sem `inherit` o tema repinta
             este Grid e o rótulo do anexo some na linha selecionada. */
          <Grid item xs={12} sx={{ backgroundColor: 'transparent', color: 'inherit' }}>
            {audio ? <Audiotrack /> : <InsertDriveFileOutlined />}
            {rotuloDoAnexo(message.archive)}
          </Grid>
        );
      }
    }

    return ret;
  }

  return (
    <Grid sx={{
      backgroundColor: props.selectedRoom === room.room_id ? '#2a4d69' : theme.palette.background.secondary,
      '&:hover': {
        backgroundColor: props.selectedRoom === room.room_id
          ? '#2a4d69'
          : (theme.palette.mode === 'dark' ? '#2c3945' : '#dde4ec'),
      },
      width: '100%',
    }} >
      <Grid
        sx={{
          backgroundColor: 'transparent', display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
        }}
        onClick={() => {
          props.handleSelectRoom(room.client, {...room, whatsapp: false});
          props.setOpenChat(false);
          setUnreadCount(0);
          messageReadFocus();
        }}>
        {/*
          As três "células" desta linha eram `TableCell`, isto é, `<td>` -- e
          não existe tabela nenhuma em volta: a linha mora dentro de uma
          célula do DataGrid, que é um `<div>`. O React avisava
          "<td> cannot appear as a child of <div>" em toda abertura da lista.
          O `td` já era tratado como bloco (o pai é flex, e flex blockifica os
          filhos), então o que ele dava era a tipografia `body2` e o padding
          padrão. É o que está escrito aqui, célula por célula: medido na
          tela, as três mantiveram posição e tamanho ao virar `Box`.
        */}
        <Box sx={{ typography: 'body2', padding: 0 }}>
          {/*
            O canal, antes de abrir a conversa.

            Ate 29/09 esta lista nao marcava canal NENHUM -- nem WhatsApp nem
            site -- e o operador so descobria por onde o cliente falava depois
            de clicar. O `WhatsAppIcon` ja estava importado em
            `RoomListTable/index.jsx` e nunca usado: a marcacao ja tinha sido
            tentada antes.

            A cor tem tres casos, conferidos nos dois temas. A linha
            selecionada e azul-escura (#2a4d69) nos DOIS temas, entao la o
            icone vai a branco, como o resto da linha. Fora da selecao o verde
            oficial (#25D366) so tem contraste no tema escuro (6.8:1); sobre o
            fundo claro (#e8ecf1) ele cai a 1.7:1 e some, por isso o tema claro
            usa o verde-petroleo do proprio WhatsApp (#128C7E, 3.5:1).
          */}
          {ehSalaDeWhatsapp(room)
            ? <WhatsAppIcon
                data-testid="icone-whatsapp"
                style={{
                  fontSize: 50,
                  color: linhaSelecionada
                    ? '#fff'
                    : (theme.palette.mode === 'dark' ? '#25D366' : '#128C7E'),
                }}
              />
            : <PermIdentity
                data-testid="icone-chat-do-site"
                style={{ fontSize: 50, color: linhaSelecionada ? '#fff' : theme.palette.text.secondary }}
              />}
        </Box>
        <Box sx={{
          typography: 'body2',
          padding: 0,
          fontSize: 14,
          fontWeight: 500,
          maxWidth: '100%', // Definir uma largura máxima para a célula
          whiteSpace: 'nowrap', // Manter o texto na mesma linha
          overflow: 'hidden', // Esconder o conteúdo que passar da célula
          textOverflow: 'ellipsis', // Adicionar "..." quando o conteúdo passar
        }}>
          <Grid sx={{ color: props.selectedRoom === room.room_id ? '#fff' : theme.palette.text.primary, fontWeight: 600, backgroundColor: 'transparent', display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {/*
              O contato pode NÃO TER EMPRESA -- e isso é o normal, não a exceção.

              Visitante é exatamente isso: quem chega por um canal de porta
              híbrida (e-mail, WhatsApp) sem informar registro entra com
              `company_id` nulo, de propósito, porque identificação nunca é
              inventada.

              Ler `client.company.code` sem proteger derrubava a LISTA INTEIRA
              com "Cannot read properties of null (reading 'code')". Não
              escondia um código: tirava o chat do ar para TODAS as conversas,
              inclusive as que estavam funcionando.

              Sem empresa, mostra só o nome. Um traço solto ali poluiria a
              linha de todo visitante para não dizer nada.
            */}
            {/*
              Nome e codigo ficam num Box que ENCOLHE (minWidth 0 + reticencias) e o
              chip abaixo NAO encolhe (flexShrink 0): com "nome - codigo" comprido
              quem cede e o nome, nunca a marca. Mesmo padrao do painel de
              atendimento.
            */}
            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {room.client?.name}
              {room.client?.company?.code ? (
                <> - <span style={{ fontWeight: 'bold' }}>{room.client.company.code}</span></>
              ) : null}
            </Box>
            {/*
              A marca por onde o cliente chegou (SysFar, MAX). Some quando nao
              ha rotulo: conversa do site nao ganha chip (o icone ja diz que e
              do site, e "Site" seria uma marca sem dado), e WhatsApp sem numero
              conhecido fica so com o icone.
            */}
            {marca ? (
              <Chip
                data-testid="chip-da-marca"
                label={marca}
                size="small"
                sx={{
                  height: 18,
                  marginLeft: 0.75,
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: linhaSelecionada
                    ? 'rgba(255,255,255,0.22)'
                    : theme.palette.action.selected,
                  color: linhaSelecionada ? '#fff' : theme.palette.text.primary,
                }}
              />
            ) : null}
          </Grid>
          {/*
            `width: 100%` e `minWidth: 0`, e não `100vw`.
            Com 100vw o espaço disponível para a prévia é a janela inteira, e
            aí o `text-overflow: ellipsis` da linha NUNCA dispara: a mensagem
            longa empurra a largura em vez de ser cortada. A largura certa é a
            da célula, que é quem manda na coluna.
          */}
          <Grid container style={{
            fontSize: 12, color: props.selectedRoom === room.room_id ? 'rgba(255,255,255,0.92)' : theme.palette.text.primary, fontWeight: 400,
            backgroundColor: 'transparent', width: '100%', minWidth: 0
          }}>
            {lastMessage()}
          </Grid>
        </Box>
        {/* O padding de 16px era o padrão do `TableCell`; sem ele a hora e o
            contador de não lidas subiriam 16px e colariam na prévia. */}
        <Box sx={{ typography: 'body2', padding: '16px', fontSize: 14, fontWeight: 400 }}>
          <Grid container alignItems="center" spacing={2} sx={{ backgroundColor: 'transparent' }}>
            <Grid item sx={{ backgroundColor: 'transparent', color: props.selectedRoom === room.room_id ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary, marginRight: unreadCount !== 0 ? '1rem' : 0 }}>
              {
                messages.length
                  ? date(messages[messages.length - 1].created_at)
                  : `${new Date().getHours()}:${new Date().getMinutes()}`
              }
            </Grid>
            <Grid item sx={{ backgroundColor: 'transparent' }}>
              <Badge badgeContent={unreadCount} color="primary" />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>

  );
});

export default Room